import type {
  GetMyDataResponseDto,
  GetMyPermissionsResponseDto,
  LoginStaffPayloadDto,
  LoginStaffResponseDto,
  RefreshTokenPayloadDto,
  RefreshTokenResponseDto,
  SendResetOTPPayloadDto,
  SignupStaffPayloadDto,
  SignupStaffResponseDto,
  UpdateSecretPayloadDto,
} from '@/types/request.dto';
import {
  EStoreThreadStatus,
  JwtDecodedEntity,
  JwtTokenEntity,
  PermissionEntity,
  RoleEntity,
  StaffEntity,
  StoreThread,
  UserEntityDto,
} from '@/types/store.entity';
import { resetPdsSession } from '@/pds/session';
import { uuidv4 } from '@/utils/uuid';
import * as jose from 'jose';
import { defineStore } from 'pinia';
import {
  computed,
  getCurrentInstance,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { ELocalStorageKey, EStoreIdentifier } from '../store.constante';
import { authService } from './auth.service';

const LOGIN_TIMEOUT_MS = 10_000;

const authStoreSetup = () => {
  const tokens = reactive<JwtTokenEntity | null>({
    accessToken: null,
    refreshToken: null,
  });
  const userStaff = ref<StaffEntity | null>({});
  const userPermissions = ref<PermissionEntity[]>([]);
  const userRoles = ref<RoleEntity[]>([]);
  const threads = ref<StoreThread<AuthStoreAction>[]>([]);

  const decodedJwt = computed(() => {
    let a, r;
    if (tokens?.accessToken)
      a = jose.decodeJwt<JwtDecodedEntity>(tokens.accessToken);
    if (tokens?.refreshToken)
      r = jose.decodeJwt<JwtDecodedEntity>(tokens.refreshToken);

    return { accessToken: a, refreshToken: r };
  });

  const connectStatus = computed<'connected' | 'disconnected' | 'loading'>(
    () => {
      const isTokenValid = decodedJwt?.value?.refreshToken?.exp
        ? decodedJwt?.value?.refreshToken?.exp * 1_000 - Date.now() > 0
        : false;

      if (isTokenValid) return 'connected';

      const isTokenLoading =
        threads.value.find(
          (thread) =>
            thread.status == EStoreThreadStatus.LOADING &&
            [
              AuthStoreAction.load_token,
              AuthStoreAction.login,
              AuthStoreAction.signup,
              AuthStoreAction.refresh,
            ].includes(thread.type),
        ) ||
        !threads.value.find(
          (thread) => thread.type == AuthStoreAction.load_token,
        );
      if (isTokenLoading) return 'loading';

      return 'disconnected';
    },
  );

  const login = async (payload: LoginStaffPayloadDto) => {
    const requestId = uuidv4();
    const abortCrt = new AbortController();

    const thread: StoreThread<AuthStoreAction> = reactive({
      id: requestId,
      status: EStoreThreadStatus.LOADING,
      type: AuthStoreAction.login,
    });
    threads.value.push(thread);
    const isError = computed(() => thread.status == EStoreThreadStatus.ERROR),
      isSuccess = computed(() => thread.status == EStoreThreadStatus.SUCCESS),
      isLoading = computed(() => thread.status == EStoreThreadStatus.LOADING);

    let result: LoginStaffResponseDto;
    const timeout = setTimeout(() => abortCrt.abort(), LOGIN_TIMEOUT_MS);
    try {
      result = await authService.login(payload, {
        requestId,
        signal: abortCrt.signal,
      });
      thread.status = EStoreThreadStatus.SUCCESS;
      if (result?.data) {
        tokens.accessToken = result.data?.tokens.accessToken;
        tokens.refreshToken = result.data?.tokens.refreshToken;

        const inLocalStorage: JwtTokenEntity = {
          accessToken: result.data?.tokens.accessToken,
          refreshToken: result.data?.tokens.refreshToken,
        };
        localStorage.setItem(
          ELocalStorageKey.JWT_TOKEN,
          JSON.stringify(inLocalStorage),
        );
      } else throw new Error();
    } catch (error) {
      thread.status = EStoreThreadStatus.ERROR;
    } finally {
      clearTimeout(timeout);
    }

    return { requestId, abortCrt, isError, isSuccess, isLoading, data: result };
  };

  const logout = () => {
    const requestId = uuidv4();
    const thread: StoreThread<AuthStoreAction> = reactive({
      id: requestId,
      status: EStoreThreadStatus.LOADING,
      type: AuthStoreAction.logout,
    });
    threads.value.push(thread);
    localStorage.removeItem(ELocalStorageKey.JWT_TOKEN);
    tokens.accessToken = null;
    tokens.refreshToken = null;
    thread.status = EStoreThreadStatus.SUCCESS;
    // Une seule redirection, sans recharger la page : le garde du layout pousse déjà sur /login, ceci l'assure
    // quel que soit l'écran. Un rechargement par-dessus faisait « actualiser » la page quelques instants après.
    resetPdsSession();
    void import('@/router/app.router').then(({ default: router }) =>
      router.replace('/login'),
    );
  };

  const refresh = async (payload: RefreshTokenPayloadDto) => {
    const requestId = uuidv4();
    const abortCrt = new AbortController();
    const thread: StoreThread<AuthStoreAction> = reactive({
      id: requestId,
      status: EStoreThreadStatus.LOADING,
      type: AuthStoreAction.refresh,
    });
    threads.value.push(thread);
    const isError = computed(() => thread.status == EStoreThreadStatus.ERROR),
      isSuccess = computed(() => thread.status == EStoreThreadStatus.SUCCESS),
      isLoading = computed(() => thread.status == EStoreThreadStatus.LOADING);

    let result: RefreshTokenResponseDto;
    try {
      result = await authService.refresh(payload, {
        requestId,
        signal: abortCrt.signal,
      });
      thread.status = EStoreThreadStatus.SUCCESS;
      if (result?.data) {
        tokens.accessToken = result.data?.token.accessToken;
        tokens.refreshToken = result.data?.token.refreshToken;

        const inLocalStorage: JwtTokenEntity = {
          accessToken: result.data?.token.accessToken,
          refreshToken: result.data?.token.refreshToken,
        };
        localStorage.setItem(
          ELocalStorageKey.JWT_TOKEN,
          JSON.stringify(inLocalStorage),
        );
      }
    } catch (error) {
      thread.status = EStoreThreadStatus.ERROR;
    }

    return { requestId, abortCrt, isError, isSuccess, isLoading, data: result };
  };

  const signup = async (payload: SignupStaffPayloadDto) => {
    const requestId = uuidv4();
    const abortCrt = new AbortController();
    const thread: StoreThread<AuthStoreAction> = reactive({
      id: requestId,
      status: EStoreThreadStatus.LOADING,
      type: AuthStoreAction.signup,
    });
    threads.value.push(thread);
    const isError = computed(() => thread.status == EStoreThreadStatus.ERROR),
      isSuccess = computed(() => thread.status == EStoreThreadStatus.SUCCESS),
      isLoading = computed(() => thread.status == EStoreThreadStatus.LOADING);

    let result: SignupStaffResponseDto;
    try {
      result = await authService.signup(payload, {
        requestId,
        signal: abortCrt.signal,
      });
      thread.status = EStoreThreadStatus.SUCCESS;
    } catch (error) {
      thread.status = EStoreThreadStatus.ERROR;
    }

    return { requestId, abortCrt, isError, isSuccess, isLoading, data: result };
  };

  const loadLocalToken = async () => {
    const requestId = uuidv4();
    const thread: StoreThread<AuthStoreAction> = reactive({
      id: requestId,
      status: EStoreThreadStatus.LOADING,
      type: AuthStoreAction.load_token,
    });
    threads.value.push(thread);
    const isError = computed(() => thread.status == EStoreThreadStatus.ERROR),
      isSuccess = computed(() => thread.status == EStoreThreadStatus.SUCCESS),
      isLoading = computed(() => thread.status == EStoreThreadStatus.LOADING);
    const localStr = localStorage.getItem(ELocalStorageKey.JWT_TOKEN);

    if (!localStr) thread.status = EStoreThreadStatus.ERROR;
    else {
      try {
        const localtoken = JSON.parse(localStr) as JwtTokenEntity;
        if (!localtoken?.accessToken || !localtoken?.refreshToken)
          throw new Error('JWT ERROR: error JSON');

        tokens.accessToken = localtoken.accessToken;
        tokens.refreshToken = localtoken.refreshToken;
        thread.status = EStoreThreadStatus.SUCCESS;
      } catch (error) {
        thread.status = EStoreThreadStatus.ERROR;
      }
    }

    return { requestId, isError, isSuccess, isLoading };
  };

  const getMyData = async () => {
    const requestId = uuidv4();
    const abortCrt = new AbortController();
    const thread: StoreThread<AuthStoreAction> = reactive({
      id: requestId,
      status: EStoreThreadStatus.LOADING,
      type: AuthStoreAction.refresh,
    });
    threads.value.push(thread);
    const isError = computed(() => thread.status == EStoreThreadStatus.ERROR),
      isSuccess = computed(() => thread.status == EStoreThreadStatus.SUCCESS),
      isLoading = computed(() => thread.status == EStoreThreadStatus.LOADING);

    let result: GetMyDataResponseDto;
    try {
      result = await authService.getMyData(undefined, {
        requestId,
        signal: abortCrt.signal,
        jwttoken: tokens.accessToken,
      });
      thread.status = EStoreThreadStatus.SUCCESS;
      if (result?.data) {
        userStaff.value =
          result.data?.user ?? (result?.data as unknown as UserEntityDto);
      }
    } catch (error) {
      thread.status = EStoreThreadStatus.ERROR;
    }

    return { requestId, abortCrt, isError, isSuccess, isLoading, data: result };
  };

  const getMyPermissions = async () => {
    const requestId = uuidv4();
    const abortCrt = new AbortController();
    const thread: StoreThread<AuthStoreAction> = reactive({
      id: requestId,
      status: EStoreThreadStatus.LOADING,
      type: AuthStoreAction.get_permissions,
    });
    threads.value.push(thread);
    const isError = computed(() => thread.status == EStoreThreadStatus.ERROR),
      isSuccess = computed(() => thread.status == EStoreThreadStatus.SUCCESS),
      isLoading = computed(() => thread.status == EStoreThreadStatus.LOADING);

    let result: GetMyPermissionsResponseDto;
    try {
      result = await authService.getMyPermissions(undefined, {
        requestId,
        signal: abortCrt.signal,
        jwttoken: tokens.accessToken,
      });
      thread.status = EStoreThreadStatus.SUCCESS;
      if (result?.data) {
        userPermissions.value = result.data.permissions ?? [];
        userRoles.value = result.data.roles ?? [];
      }
    } catch (error) {
      thread.status = EStoreThreadStatus.ERROR;
    }

    return { requestId, abortCrt, isError, isSuccess, isLoading, data: result };
  };

  const sendResetOTP = async (payload: SendResetOTPPayloadDto) => {
    const requestId = uuidv4();
    const abortCrt = new AbortController();
    const thread: StoreThread<AuthStoreAction> = reactive({
      id: requestId,
      status: EStoreThreadStatus.LOADING,
      type: AuthStoreAction.request_otp,
    });
    threads.value.push(thread);
    const isError = computed(() => thread.status == EStoreThreadStatus.ERROR),
      isSuccess = computed(() => thread.status == EStoreThreadStatus.SUCCESS),
      isLoading = computed(() => thread.status == EStoreThreadStatus.LOADING);

    let result: boolean;
    try {
      result = await authService.sendResetOTP(payload, {
        requestId,
        signal: abortCrt.signal,
      });
      thread.status = EStoreThreadStatus.SUCCESS;
    } catch (error) {
      thread.status = EStoreThreadStatus.ERROR;
    }

    return { requestId, abortCrt, isError, isSuccess, isLoading, data: result };
  };
  const updateSecret = async (payload: UpdateSecretPayloadDto) => {
    const requestId = uuidv4();
    const abortCrt = new AbortController();
    const thread: StoreThread<AuthStoreAction> = reactive({
      id: requestId,
      status: EStoreThreadStatus.LOADING,
      type: AuthStoreAction.update_secret,
    });
    threads.value.push(thread);
    const isError = computed(() => thread.status == EStoreThreadStatus.ERROR),
      isSuccess = computed(() => thread.status == EStoreThreadStatus.SUCCESS),
      isLoading = computed(() => thread.status == EStoreThreadStatus.LOADING);

    let result: GetMyDataResponseDto;
    try {
      result = await authService.updateSecret(payload, {
        requestId,
        signal: abortCrt.signal,
      });
      thread.status = EStoreThreadStatus.SUCCESS;
    } catch (error) {
      thread.status = EStoreThreadStatus.ERROR;
    }

    return { requestId, abortCrt, isError, isSuccess, isLoading, data: result };
  };

  // Helper pour automatiser le refresh token
  // 10 min avant expiration
  watch(
    [() => tokens],
    (value, _, onCleanup) => {
      let timer: any;
      const timeBeforeRefresh = 10 * 60 * 1_000; //10 mins

      if (value[0]?.accessToken && value[0]?.refreshToken) {
        const exp = decodedJwt.value?.accessToken?.exp * 1000;
        const refreshToken = value[0].refreshToken;
        const refreshAt = exp < 0 ? 0 : exp - timeBeforeRefresh;
        const now = Date.now();

        timer = window.setTimeout(
          () => refresh({ refreshToken }),
          refreshAt - now,
        );
      }

      onCleanup(() => {
        if (timer) window.clearTimeout(timer);
      });
    },
    { deep: true },
  );

  // Helper pour automatiquement get userInfo
  watch(
    [() => tokens?.accessToken],
    ([accessToken]) => {
      if (!accessToken) return;
      getMyData();
      getMyPermissions();
    },
    { immediate: true },
  );

  if (getCurrentInstance()) {
    onMounted(() => {
      if (!tokens?.accessToken) loadLocalToken().catch(null);
    });
  }

  return {
    login,
    logout,
    getMyData,
    getMyPermissions,
    signup,
    refresh,
    tokens,
    userStaff,
    userPermissions,
    userRoles,
    threads,
    decodedJwt,
    loadLocalToken,
    connectStatus,
    sendResetOTP,
    updateSecret,
  };
};

const useAuthStore = defineStore(
  EStoreIdentifier.AUTH_STORE as const,
  authStoreSetup,
);

export enum AuthStoreAction {
  login = 'auth/login',
  logout = 'auth/logout',
  signup = 'auth/signup',
  refresh = 'auth/refresh',
  load_token = 'auth/load_token',
  request_otp = 'auth/request_otp',
  update_secret = 'auth/update_secret',
  get_permissions = 'auth/get_permissions',
}

export { useAuthStore };
